import { FC } from 'react';
import intl from 'react-intl-universal';
import get from 'lodash/get';
import capitalize from 'lodash/capitalize';
import moment from 'utils/moment';
import { PATHS, DATE } from 'utils/constants';
import { TaskWithAssigneesAndProject } from 'types/store/tasksList';
import useDueTasks from './useDueTasks';
import MyWorkspaceTable from '../MyWorkspaceTable';
import {
  getStatusColumn,
  getStatusPill,
} from 'Pages/ProjectsListPage/helpers/tableColumnsValues';
import QuickHoursAddingAction from 'Organisms/QuickHoursAddingAction';
import TruncatedTableColumn from 'Pages/ProjectsListPage/components/ActiveProjects/components/TruncatedTableColumn/TruncatedTableColumn';
import { LinkTableRowCell } from 'Atoms/LinkTableRowCell';
import { PilotPagesToTrack } from 'utils/customTypes';
import { StatusTableRowCell } from 'Atoms/EditableTableRowCell/StatusTableRowCell';
import {
  decimalHoursToTimeString,
  tailwindOverride,
} from '@getsynapse/design-system';
import { useUpdateStatusCell } from './hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';

type TasksTableProps = {
  cardTable?: boolean;
  limit?: number;
};

type GetStatusComponentProps = {
  task: TaskWithAssigneesAndProject;
  canEditStatus: boolean;
  isOnlyStatusViewMode: boolean;
  valueToShow: (value: string) => JSX.Element;
  className?: string;
};

const TasksTable: FC<TasksTableProps> = ({ cardTable = true, limit }) => {
  const { handleSaveTaskStatus, getStatuses } = useUpdateStatusCell();

  const { sortedTasks, setTableSorting, refreshTasks } = useDueTasks(limit);
  const { flexibleTaskTimeEntry } = useFlags();

  const handleSort = (
    orderByParam: string[] | string,
    order: 'desc' | 'asc'
  ) => {
    const sortBy = orderByParam as string;
    setTableSorting({ sortBy, order });
  };

  const createLinkToTheTask = (task: TaskWithAssigneesAndProject) => {
    return `${PATHS.PROJECT_PAGE}/${get(task, 'project.id')}${PATHS.TASKS}/${
      task.id
    }`;
  };

  const createDueDateComponent = (endDate: string) => {
    const dueDateWithoutTime = moment(new Date(endDate!))
      .locale('en')
      .format('L');
    const currentDate = moment(new Date());
    const currentTime = currentDate.format('HH:mm:ssZ');
    const momentDueDate = moment(`${dueDateWithoutTime} ${currentTime}`);
    let dueDateFromNow = momentDueDate.fromNow();

    if (
      dueDateFromNow.includes('hour') ||
      dueDateFromNow.includes('minute') ||
      dueDateFromNow.includes('second')
    ) {
      dueDateFromNow = intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.TODAY');
    }

    const dueDate = cardTable
      ? dueDateFromNow
      : momentDueDate.format(DATE.SHORT_FORMAT);
    const dDiff = momentDueDate.diff(currentDate, 'days');
    const daysBetween = currentDate.date() - momentDueDate.date();
    let dueDateStyle = '';

    if (dDiff < 0) {
      dueDateStyle = 'text-error-darker';
      if (!cardTable) {
        dueDateStyle = dueDateStyle + ' bg-error-lightest';
      }
    } else if (dDiff === 0 && daysBetween === 0) {
      dueDateStyle = 'text-warning-darker';
    }

    return (
      <TruncatedTableColumn
        content={capitalize(dueDate)}
        className={dueDateStyle}
      />
    );
  };

  const getActualHours = (hours?: string) => {
    if (flexibleTaskTimeEntry) {
      return decimalHoursToTimeString(parseFloat(hours || '0'));
    }

    return hours ? `${parseFloat(hours)}h` : '0h';
  };

  const getStatusComponent = ({
    task,
    canEditStatus,
    isOnlyStatusViewMode,
    valueToShow,
    className,
  }: GetStatusComponentProps) => {
    return (
      <StatusTableRowCell
        value={task.status}
        customizedValue={valueToShow}
        options={getStatuses(task)}
        isEditable={!isOnlyStatusViewMode}
        className={className}
        isTaskProjectDisabled={false}
        canInlineEditTask={!task.disabled}
        onSave={(newValue) => {
          handleSaveTaskStatus(newValue, canEditStatus, task);
        }}
        canEdit={canEditStatus}
        userPilotEventOrigin={PilotPagesToTrack.HOME}
      />
    );
  };

  const getStatusColumnDependsOnFlag = (
    task: TaskWithAssigneesAndProject,
    canEditStatus: boolean,
    isOnlyStatusViewMode: boolean
  ) => {
    if (cardTable) {
      return getStatusComponent({
        task,
        canEditStatus,
        isOnlyStatusViewMode,
        valueToShow: getStatusPill,
        className: 'w-20',
      });
    } else {
      return getStatusComponent({
        task,
        canEditStatus,
        isOnlyStatusViewMode,
        valueToShow: (status: string) => (
          <>{getStatusColumn(status as any, false, '', true, canEditStatus)}</>
        ),
        className: 'w-32',
      });
    }
  };

  return (
    <MyWorkspaceTable
      cardTable={cardTable}
      dataCy='due-tasks-table'
      headCells={[
        {
          content: intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.HEAD_CELLS.NAME'),
          columnName: 'name',
        },
        {
          content: intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.HEAD_CELLS.STATUS'),
          columnName: 'status',
          className: tailwindOverride('w-28', {
            'w-20': cardTable,
            'p-0 w-32': !cardTable,
          }),
        },
        {
          content: cardTable
            ? intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.HEAD_CELLS.DUE')
            : intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.HEAD_CELLS.DUE_DATE'),
          columnName: 'endDate',
          className: 'w-24',
        },
        {
          content: intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.HEAD_CELLS.ACTUALS'),
          columnName: false,
          className: tailwindOverride('w-16', {
            'w-24': flexibleTaskTimeEntry,
          }),
        },
        {
          content: '',
          columnName: false,
          className: 'w-12',
        },
      ]}
      rows={sortedTasks.map((task: TaskWithAssigneesAndProject) => {
        const dueDateComponent = createDueDateComponent(task.endDate);
        const hasPermission = task.permissions?.canEdit;
        const canEditStatus = !task.disabled && hasPermission;

        const isOnlyStatusViewMode =
          !task.permissions?.canEdit || !canEditStatus;

        return {
          id: task.id,
          'aria-label': `due-task-${task.id}`,
          className: tailwindOverride('table w-full table-fixed group'),
          cells: [
            {
              content: (
                <div className='flex flex-col'>
                  <LinkTableRowCell
                    value={task.name}
                    to={createLinkToTheTask(task)}
                    className='font-semibold truncate leading-6'
                  />

                  <span
                    title={get(task, 'project.title')}
                    className='overflow-hidden text-dark-gray truncate'
                  >
                    {get(task, 'project.title')}
                  </span>
                </div>
              ),
              className: 'truncate',
            },
            {
              content: getStatusColumnDependsOnFlag(
                task,
                canEditStatus,
                isOnlyStatusViewMode
              ),
              className: tailwindOverride('w-28 p-0', {
                'w-20': cardTable,
                'p-0 w-32': !cardTable,
              }),
              'aria-label': `due-task-status`,
            },
            {
              content: dueDateComponent,
              className: 'w-24',
            },
            {
              content: (
                <span className='text-sm'>
                  {getActualHours(task.actualHours)}
                </span>
              ),
              className: tailwindOverride('w-16', {
                'w-24': flexibleTaskTimeEntry,
              }),
            },
            {
              content: (
                <QuickHoursAddingAction
                  task={task}
                  refreshTasks={refreshTasks}
                  userPilotEventOrigin={PilotPagesToTrack.HOME}
                />
              ),
              className: 'w-12',
            },
          ],
        };
      })}
      handleSort={handleSort}
    />
  );
};

export default TasksTable;
